/**
 * Whether a rule's regex can be published: it must compile, and mean the same in
 * this library (`new RegExp(value, flags)`) and in the server's PCRE port.
 *
 * A line-for-line port of `RegexConverter::problems()`
 * (fonterra/src/Events/SourceAssignment/Assignment/RegexConverter.php), so the
 * client validator refuses exactly what saving a rule set refuses (BACKLOG.md
 * §9.6, §9.13 row 11). Both read the shared pattern list in
 * fixtures/source-assignment/regex-portability.json in their tests, so the two
 * cannot drift apart unnoticed. Messages match the PHP ones word for word.
 *
 * Conservative on purpose: a construct is accepted only where both engines read
 * it the same, so some valid ECMAScript (`\u0041`) is refused rather than guessed.
 * The compile check here is ECMAScript's; PHP's is PCRE's. A pattern only one of
 * them can compile (rare: PCRE refuses some long lookbehinds) is refused on save
 * by the server, which is the gate that matters.
 */

/** The flags both engines read the same way. `g`/`y` make RegExp.test() stateful. */
export const REGEX_FLAGS = 'imsu';

const PORTABLE_LETTER_ESCAPES = 'dDwWsSbBnrtf';
const SYNTAX_CHARACTERS = '^$\\.*+?()[]{}|/';

const isDigit = (c: string) => c >= '0' && c <= '9';
const isAlpha = (c: string) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');
const isHexDigit = (c: string) => isDigit(c) || (c >= 'a' && c <= 'f') || (c >= 'A' && c <= 'F');

export function regexProblems(pattern: string, flags: string = ''): Array<string> {
    let problems = flagProblems(flags);
    if (problems.length) {
        return problems;
    }
    problems = portabilityProblems(pattern, flags.indexOf('u') !== -1);
    if (problems.length) {
        return problems;
    }
    try {
        new RegExp(pattern, flags);
    } catch (error) {
        return [`Regex '${pattern}' does not compile: ${(error as Error).message}.`];
    }
    return [];
}

function flagProblems(flags: string): Array<string> {
    if (flags === '') {
        return [];
    }
    const letters = flags.split('');
    const unknown = letters.some((l) => REGEX_FLAGS.indexOf(l) === -1);
    const repeated = letters.some((l, i) => letters.indexOf(l) !== i);
    if (unknown || repeated) {
        return [`Regex flags '${flags}' are not allowed: use each of ${REGEX_FLAGS} at most once.`];
    }
    return [];
}

function portabilityProblems(pattern: string, unicode: boolean): Array<string> {
    const problems: Array<string> = [];
    const add = (problem: string) => {
        if (problems.indexOf(problem) === -1) {
            problems.push(problem);
        }
    };
    let inClass = false;
    let i = 0;
    while (i < pattern.length) {
        const char = pattern.charAt(i);
        const next = pattern.charAt(i + 1);

        if (char === '\\') {
            if (next === '') {
                add(`Regex '${pattern}' ends with a lone backslash.`);
                break;
            }
            const problem = escapeProblem(pattern, i, unicode, inClass);
            if (problem !== null) {
                add(problem);
            }
            i += 2;
            continue;
        }

        if (inClass) {
            if (char === '[' && (next === ':' || next === '.' || next === '=')) {
                add(`Regex '${pattern}' uses a POSIX class ([${next}...${next}]), `
                    + 'which ECMAScript reads as literal characters.');
            }
            if (char === ']') {
                inClass = false;
            }
            i++;
            continue;
        }

        if (char === '[') {
            if (next === ']' || (next === '^' && pattern.charAt(i + 2) === ']')) {
                const empty = next === ']' ? '[]' : '[^]';
                add(`Regex '${pattern}' uses ${empty}, which PCRE reads differently from ECMAScript.`);
            }
            inClass = true;
            i++;
            continue;
        }

        if (char === '(' && next === '*') {
            add(`Regex '${pattern}' uses a PCRE verb (*...), which ECMAScript does not support.`);
        } else if (char === '(' && next === '?' && !isPortableGroup(pattern.substr(i + 2, 2))) {
            add(`Regex '${pattern}' uses (?${pattern.substr(i + 2, 1)}`
                + ', which ECMAScript does not support; use flags instead of inline modifiers.');
        } else if (char === '{' && next === ',') {
            add(`Regex '${pattern}' uses {,n}, a quantifier in PCRE but literal text in ECMAScript.`);
        } else if (next === '+' && endsAQuantifier(pattern, i)) {
            add(`Regex '${pattern}' uses a possessive quantifier (${char}+), `
                + 'which ECMAScript does not support.');
        }
        i++;
    }
    return problems;
}

function escapeProblem(pattern: string, at: number, unicode: boolean, inClass: boolean): string | null {
    const escaped = pattern.charAt(at + 1);
    if (isDigit(escaped)) {
        return null;
    }
    if (!isAlpha(escaped)) {
        if (unicode && SYNTAX_CHARACTERS.indexOf(escaped) === -1 && !(inClass && escaped === '-')) {
            return `Regex '${pattern}' uses \\${escaped}, which ECMAScript refuses under the u flag.`;
        }
        return null;
    }
    if (PORTABLE_LETTER_ESCAPES.indexOf(escaped) !== -1) {
        return null;
    }
    if (escaped === 'x') {
        const hex = pattern.substr(at + 2, 2);
        return hex.length === 2 && isHexDigit(hex.charAt(0)) && isHexDigit(hex.charAt(1))
            ? null
            : `Regex '${pattern}' uses \\x without exactly two hex digits (\\x{...} is PCRE-only).`;
    }
    if (escaped === 'c') {
        return isAlpha(pattern.charAt(at + 2)) ? null : `Regex '${pattern}' uses \\c without a control letter.`;
    }
    if (escaped === 'k') {
        return pattern.charAt(at + 2) === '<' ? null : `Regex '${pattern}' uses \\k without <name>.`;
    }
    if (escaped === 'p' || escaped === 'P') {
        return unicode
            ? null
            : `Regex '${pattern}' uses \\${escaped}, which means a Unicode property only with the u flag.`;
    }
    return `Regex '${pattern}' uses \\${escaped}, which means different things in ECMAScript and PCRE.`;
}

/** After `(?`: non-capturing, lookahead, lookbehind, or a named group. */
function isPortableGroup(after: string): boolean {
    const first = after.charAt(0);
    if (first === ':' || first === '=' || first === '!') {
        return true;
    }
    if (first !== '<') {
        return false;
    }
    const second = after.charAt(1);
    return second === '=' || second === '!' || isAlpha(second) || second === '_';
}

/** Whether the character at `at` closes a quantifier; a `+` right after one makes it possessive. */
function endsAQuantifier(pattern: string, at: number): boolean {
    const char = pattern.charAt(at);
    if (char === '*' || char === '+' || char === '?') {
        // the scan steps over escapes, so a quantifier character reached here is real
        return true;
    }
    return char === '}' && /\{\d+(,\d*)?$/.test(pattern.substring(0, at));
}
